/**
 * Connection bridge: Trystero room → core/ modules.
 *
 * Accepts a Trystero Room and wires its peer join/leave events into the
 * core MeshManager and ProtocolManager, creating DataChannels and
 * signaling state for each peer.
 *
 * When a crypto Identity is provided, also wires E2E encryption:
 * setup on init, addPeer on LIVE, removePeer on leave, teardown on cleanup.
 */

import type { Room } from 'trystero'
import type {
  Identity,
  SenderKeyDistributeMessage,
  SenderKeyRotateMessage,
  MeetingStateSyncMessage,
  MeetingStateSyncParticipant,
} from './types'
import { createMeshManager, type MeshManager, type MeshEvents } from './core/mesh'
import {
  createProtocolManager,
  type ProtocolManager,
  type ProtocolEvents,
  type LocalIdentity,
} from './core/signaling'
import { createEnvelope, sendMessage } from './core/datachannel'
import {
  importKxPublicKeyFromBase64,
  arrayBufferToBase64Url,
  base64UrlToArrayBuffer,
} from './crypto/identity'
import {
  setupEncryption,
  handleReceivedKey,
  teardownEncryption,
  type EncryptionManager,
} from './encryption'
import {
  applyEncryptionTransform,
  applyDecryptionTransform,
  removeTransforms,
  removeAllTransforms,
} from './media/transforms'
import {
  getAllParticipants,
  addParticipant,
  getParticipant,
} from './state/participants'

// ============================================================
// Types
// ============================================================

export interface ConnectionBridge {
  mesh: MeshManager
  protocol: ProtocolManager
  encryption: EncryptionManager | null
  destroy(): void
}

export interface ConnectionBridgeEvents {
  mesh?: MeshEvents
  protocol?: ProtocolEvents
}

// ============================================================
// Module state
// ============================================================

let activeBridge: ConnectionBridge | null = null

// ============================================================
// Setup
// ============================================================

/**
 * Wire a Trystero room into the core/ module stack.
 *
 * On each peer join, this:
 * 1. Creates a PeerConnection via the MeshManager
 * 2. Registers the peer in the ProtocolManager with its DataChannels
 * 3. Attaches signaling handlers
 * 4. Initiates the hello handshake
 *
 * On each peer leave, cleans up both mesh and protocol state.
 *
 * When `cryptoIdentity` is provided, E2E encryption is wired into the
 * peer lifecycle: keys are exchanged on LIVE and rotated on leave.
 */
export function setupConnections(
  room: Room,
  identity: LocalIdentity,
  events: ConnectionBridgeEvents = {},
  cryptoIdentity?: Identity,
): ConnectionBridge {
  // Tear down any existing bridge
  if (activeBridge) {
    activeBridge.destroy()
  }

  let encMgr: EncryptionManager | null = null

  // Initialize encryption if crypto identity is provided
  const encryptionReady = cryptoIdentity
    ? setupEncryption(cryptoIdentity).then((mgr) => {
        encMgr = mgr
        return mgr
      })
    : Promise.resolve(null)

  const meshEvents: MeshEvents = {
    ...events.mesh,
    onTrack: (peerId, event) => {
      // Apply decryption transforms to incoming receivers when encryption is active
      if (cryptoIdentity) {
        encryptionReady.then((mgr) => {
          if (!mgr) return
          const sframeWorker = mgr.getWorker()
          if (sframeWorker && event.receiver) {
            applyDecryptionTransform(event.receiver, peerId, sframeWorker)
          }
        }).catch((err) => {
          console.error(`[connection] Failed to apply decryption transform for ${peerId}:`, err)
        })
      }
      events.mesh?.onTrack?.(peerId, event)
    },
  }
  const mesh = createMeshManager(identity.peerId, meshEvents)

  const protocolEvents: ProtocolEvents = {
    ...events.protocol,
    onPeerLive: (peerId) => {
      mesh.startHeartbeats()

      // Send current meeting state to the newly-live peer (late-joiner sync)
      sendMeetingStateToNewPeer(peerId, identity, protocol)

      // Exchange encryption keys when peer reaches LIVE
      if (cryptoIdentity) {
        exchangeKeysWithPeer(peerId, identity, protocol, mesh, encryptionReady)
      }

      events.protocol?.onPeerLive?.(peerId)
    },
    // Handle meeting-state-sync from an existing peer (we are the late joiner)
    onMeetingStateSync: (peerId, msg) => {
      const syncMsg = msg as MeetingStateSyncMessage
      handleMeetingStateSync(syncMsg)
      events.protocol?.onMeetingStateSync?.(peerId, msg)
    },
    // Wire crypto DataChannel message handlers for key distribution
    onSenderKeyDistribute: (peerId, msg) => {
      const skMsg = msg as SenderKeyDistributeMessage
      const keyData = base64UrlToArrayBuffer(skMsg.payload.keyData)
      handleReceivedKey(peerId, skMsg.payload.epoch, keyData).catch((err) => {
        console.error(`[connection] Failed to handle sender key from ${peerId}:`, err)
      })
      events.protocol?.onSenderKeyDistribute?.(peerId, msg)
    },
    onSenderKeyRotate: (peerId, msg) => {
      const skMsg = msg as SenderKeyRotateMessage
      const keyData = base64UrlToArrayBuffer(skMsg.payload.keyData)
      handleReceivedKey(peerId, skMsg.payload.epoch, keyData).catch((err) => {
        console.error(`[connection] Failed to handle rotated key from ${peerId}:`, err)
      })
      events.protocol?.onSenderKeyRotate?.(peerId, msg)
    },
  }
  const protocol = createProtocolManager(identity, protocolEvents, mesh)

  // --- Trystero peer join → core/ setup ---
  room.onPeerJoin((peerId) => {
    // 1. Create PeerConnection + DataChannels via mesh
    const handle = mesh.addPeer(peerId)
    if (!handle) return // at capacity

    // 2. Register peer in protocol state machine with its DataChannels
    protocol.addPeer(peerId, handle.channels)

    // 3. Attach signaling message handlers on DataChannels
    protocol.attachHandlers(peerId)

    // 4. Initiate hello handshake
    protocol.sendHello(peerId)
  })

  // --- Trystero peer leave → cleanup ---
  room.onPeerLeave((peerId) => {
    protocol.removePeer(peerId)
    mesh.removePeer(peerId, 'left')

    // Remove SFrame transforms for this peer
    removeTransforms(peerId)

    // Remove encrypted peer and redistribute rotated keys
    if (encMgr) {
      encMgr.removeEncryptedPeer(peerId).then((reKeyPayloads) => {
        // Send rotated keys to all remaining peers
        for (const [remainingPeerId, payload] of reKeyPayloads) {
          const peerState = protocol.getState(remainingPeerId)
          if (peerState?.channels) {
            const envelope = createEnvelope('sender-key-rotate', identity.peerId, {
              epoch: payload.epoch,
              senderId: 0,
              keyData: arrayBufferToBase64Url(payload.encryptedKey),
              algorithm: 'aes-gcm-256',
              iv: '',
              signature: '',
              reason: 'participant-left' as const,
              triggerPeerId: peerId,
              prevEpochExpiry: Date.now() + 5000,
            })
            try {
              sendMessage(peerState.channels.crypto, envelope)
            } catch {
              // Crypto channel may not be open
            }
          }
        }
      }).catch((err) => {
        console.error(`[connection] Failed to rotate keys after ${peerId} left:`, err)
      })
    }
  })

  activeBridge = {
    mesh,
    protocol,
    encryption: encMgr,
    destroy: () => destroyBridge(mesh, protocol),
  }
  return activeBridge
}

// ============================================================
// Encryption helpers
// ============================================================

/**
 * Exchange encryption keys with a peer that just reached LIVE state.
 * Imports their ECDH public key from the hello payload and sends
 * our encrypted sender key over the crypto DataChannel.
 */
async function exchangeKeysWithPeer(
  peerId: string,
  identity: LocalIdentity,
  protocol: ProtocolManager,
  mesh: MeshManager,
  encryptionReady: Promise<EncryptionManager | null>,
): Promise<void> {
  try {
    const mgr = await encryptionReady
    if (!mgr) return

    const peerState = protocol.getState(peerId)
    if (!peerState?.helloPayload?.publicKey || !peerState.channels) return

    // Import the peer's ECDH public key from base64url
    const peerPublicKey = await importKxPublicKeyFromBase64(peerState.helloPayload.publicKey)

    // Add encrypted peer and get key payloads
    const result = await mgr.addEncryptedPeer(peerId, peerPublicKey)

    // Send our encrypted sender key to the new peer over crypto DataChannel
    const envelope = createEnvelope('sender-key-distribute', identity.peerId, {
      epoch: result.newPeerPayload.epoch,
      senderId: 0,
      keyData: arrayBufferToBase64Url(result.newPeerPayload.encryptedKey),
      algorithm: 'aes-gcm-256',
      iv: '',
      signature: '',
    })
    sendMessage(peerState.channels.crypto, envelope)

    // Apply encryption transforms to outgoing senders for this peer
    const sframeWorker = mgr.getWorker()
    if (sframeWorker) {
      const peerHandle = mesh.getPeer(peerId)
      if (peerHandle) {
        for (const sender of peerHandle.pc.getSenders()) {
          if (sender.track) {
            applyEncryptionTransform(sender, peerId, sframeWorker)
          }
        }
      }
    }

    // Send re-keyed payloads to existing peers (forward secrecy)
    for (const [existingPeerId, reKeyPayload] of result.reKeyPayloads) {
      const existingState = protocol.getState(existingPeerId)
      if (existingState?.channels) {
        const reKeyEnvelope = createEnvelope('sender-key-rotate', identity.peerId, {
          epoch: reKeyPayload.epoch,
          senderId: 0,
          keyData: arrayBufferToBase64Url(reKeyPayload.encryptedKey),
          algorithm: 'aes-gcm-256',
          iv: '',
          signature: '',
          reason: 'participant-joined' as const,
          triggerPeerId: peerId,
          prevEpochExpiry: Date.now() + 5000,
        })
        try {
          sendMessage(existingState.channels.crypto, reKeyEnvelope)
        } catch {
          // Crypto channel may not be open
        }
      }
    }
  } catch (err) {
    console.error(`[connection] Failed to exchange keys with ${peerId}:`, err)
  }
}


// ============================================================
// Late-joiner meeting state sync
// ============================================================

/**
 * Send the current meeting state to a peer that just reached LIVE.
 * Existing participants gather their known peers and send a
 * meeting-state-sync message so the late joiner can populate its store.
 */
function sendMeetingStateToNewPeer(
  newPeerId: string,
  identity: LocalIdentity,
  protocol: ProtocolManager,
): void {
  const peerState = protocol.getState(newPeerId)
  if (!peerState?.channels) return

  // Gather participants from the store (includes ourselves and all known peers)
  const allParticipants = getAllParticipants()
  const participants: MeetingStateSyncParticipant[] = allParticipants.map((p) => ({
    peerId: p.peerId,
    publicKey: p.publicKey ?? '',
    displayName: p.displayName,
    joinedAt: p.joinedAt,
    audioMuted: p.audioMuted,
    videoMuted: p.videoMuted,
    capabilities: p.capabilities ?? {
      sframe: false,
      svc: [],
      maxVideoTracks: 1,
      canRelay: false,
    },
  }))

  // Only send if we have participants to report
  if (participants.length === 0) return

  const envelope = createEnvelope('meeting-state-sync', identity.peerId, {
    meetingId: identity.meetingId,
    epoch: Date.now(),
    participants,
    initiatorPeerId: identity.peerId,
  })

  try {
    sendMessage(peerState.channels.signaling, envelope)
  } catch (err) {
    console.error(`[connection] Failed to send meeting-state-sync to ${newPeerId}:`, err)
  }
}

/**
 * Handle an incoming meeting-state-sync message (we are the late joiner).
 * Populate the participant store with peers we don't already know about.
 */
function handleMeetingStateSync(msg: MeetingStateSyncMessage): void {
  const { participants } = msg.payload

  for (const p of participants) {
    // Skip if we already have this participant
    if (getParticipant(p.peerId)) continue

    addParticipant(p.peerId, {
      displayName: p.displayName,
      audioMuted: p.audioMuted,
      videoMuted: p.videoMuted,
      connectionState: 'connected',
      isSpeaking: false,
      audioLevel: 0,
      joinedAt: p.joinedAt,
      publicKey: p.publicKey || null,
      capabilities: p.capabilities,
    })
  }

  console.log(
    `[connection] Meeting state sync: received ${participants.length} participants from ${msg.from}`,
  )
}

// ============================================================
// Teardown
// ============================================================

/**
 * Tear down the active connection bridge, cleaning up all peer
 * connections, DataChannels, and protocol state.
 */
export function teardownConnections(): void {
  if (activeBridge) {
    activeBridge.destroy()
    activeBridge = null
  }
}

function destroyBridge(mesh: MeshManager, protocol: ProtocolManager): void {
  // Clean up protocol state for all peers
  for (const peerId of protocol.peers().keys()) {
    protocol.removePeer(peerId)
  }
  // Remove all SFrame transforms before closing connections
  removeAllTransforms()
  // Destroy mesh (closes all PeerConnections, stops heartbeats)
  mesh.destroy()
  // Tear down encryption (terminate worker, clear keys)
  teardownEncryption()
}
